import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Edge } from '@sierralabs/workflow';

import { useTimeout } from '../../../common/timeout.hook';
import { Connector, Entity, EntityManager } from './connector';

const StyledSvgGroup = styled.g`
  path {
    cursor: pointer;
  }
`;

function getPathBetweenEdges(edges: Edge[]): string {
  let result: string = '';
  const halfDistance: number =
    Math.abs(edges.length > 0 ? edges[0].x - edges[edges.length - 1].x : 0) / 2;
  for (let index: number = 0; index < edges.length; index++) {
    const edge: Edge = edges[index];
    if (index === 0) {
      result = `M${edge.x} ${edge.y}`;
      continue;
    }

    const next: Edge | undefined = edges[index + 1];
    const next2: Edge | undefined = edges[index + 2];
    if (next && next2) {
      // apply curve using the next 2 edges
      result += ` C${edge.x + halfDistance} ${edge.y}`;
      result += ` ${next.x - halfDistance} ${next.y}`;
      result += ` ${next2.x} ${next2.y}`;
      // skip 2 edges
      index += 2;
    } else {
      result += ` L${edge.x} ${edge.y}`;
    }
  }
  return result;
}

export interface ProductBoardConnectorProps {
  from: string;
  to: string;
  manager: EntityManager;
}

const ProductBoardConnector: React.FC<ProductBoardConnectorProps> = (
  props: ProductBoardConnectorProps
) => {
  const { from, to, manager } = props;
  const pathRef = useRef<SVGPathElement | null>(null);
  const [debounceSet] = useTimeout();
  const [connector, setConnector] = useState<Connector | undefined>();

  const handleCalculate = () => {
    if (!connector) {
      return;
    }
    const edges: Edge[] = connector.calculateEdges();
    const path: string = getPathBetweenEdges(edges);
    if (pathRef.current) {
      pathRef.current.setAttribute('d', path);
    }
  };

  useLayoutEffect(() => {
    return debounceSet(() => {
      const fromEntity: Entity | undefined = manager.addEntity(from);
      const toEntity: Entity | undefined = manager.addEntity(to);
      const fromEl = fromEntity?.el;
      const toEl = toEntity?.el;
      if (!(fromEntity && toEntity && fromEl && toEl)) {
        return;
      }

      const connector: Connector = fromEntity.connect(toEntity);
      connector.setObserverCallback(handleCalculate);
      setConnector(connector);
      console.log(
        'connect %s -> %s',
        from,
        to,
        '\n  from:',
        fromEntity,
        '\n  to: ',
        toEntity,
        '\n  connector:',
        connector
      );
    }, 100);
  });

  useLayoutEffect(() => {
    if (connector) {
      handleCalculate();
      connector.startObserver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector]);

  if (!connector) {
    return <></>;
  }

  return (
    <StyledSvgGroup data-source-entity={from} data-target-entity={to}>
      <path
        ref={pathRef}
        key='connector'
        data-class='connector'
        // d={path}
        fill='none'
        stroke='#007aff'
        strokeWidth='2.5'
        strokeDasharray='4,4'
      />
    </StyledSvgGroup>
  );
};

export default ProductBoardConnector;
